import { Input, Modal, Radio, Select, Space } from 'antd';
import { useEffect, useState } from 'react';

const { TextArea } = Input;
const { Option } = Select;

export const REVIEW_STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

export const REVIEW_STATUS_LABELS = {
  PENDING: 'Unreviewed',
  SUCCESS: 'Success',
  FAILURE: 'Failure',
};

export const REVIEW_STATUS_COLORS = {
  PENDING: 'gold',
  SUCCESS: 'green',
  FAILURE: 'red',
};

export const FAILURE_REASONS = [
  { value: 'WRONG_INFO', label: 'Wrong info' },
  { value: 'MISUNDERSTOOD_INTENT', label: 'Misunderstood intent' },
  { value: 'INCOMPLETE_ANSWER', label: 'Incomplete answer' },
  { value: 'HALLUCINATION', label: 'Hallucination' },
  { value: 'WRONG_LANGUAGE', label: 'Wrong language' },
  { value: 'TONE', label: 'Tone' },
  { value: 'TECHNICAL_ERROR', label: 'Technical error' },
  { value: 'OTHER', label: 'Other' },
];

export const getEffectiveReviewStatus = (conversation) =>
  (conversation && conversation.reviewStatus) || REVIEW_STATUS.PENDING;

export const getReviewStatusLabel = (status) =>
  REVIEW_STATUS_LABELS[status || REVIEW_STATUS.PENDING] || status || REVIEW_STATUS_LABELS.PENDING;

export const getFailureReasonLabel = (reason) => {
  const match = FAILURE_REASONS.find((item) => item.value === reason);
  return match ? match.label : reason || '-';
};

export const getReviewerFromCookies = (allCookies) => {
  const user = allCookies && allCookies.user ? allCookies.user : {};
  return {
    id: user.id || null,
    name: user.name || user.email || user.id || null,
  };
};

export const buildReviewUpdateData = ({ reviewStatus, failureReason, failureNote }, allCookies) => {
  const reviewer = getReviewerFromCookies(allCookies);
  const isFailure = reviewStatus === REVIEW_STATUS.FAILURE;
  const note = isFailure && failureNote ? failureNote.trim() : '';

  return {
    reviewStatus: { set: reviewStatus },
    failureReason: { set: isFailure ? failureReason : null },
    failureNote: { set: note || null },
    reviewedById: { set: reviewer.id },
    reviewedByName: { set: reviewer.name },
    reviewedAt: { set: new Date().toISOString() },
  };
};

function AssistantConversationReviewModal({ conversation, visible, loading, onCancel, onSave }) {
  const [reviewStatus, setReviewStatus] = useState(REVIEW_STATUS.SUCCESS);
  const [failureReason, setFailureReason] = useState(undefined);
  const [failureNote, setFailureNote] = useState('');

  useEffect(() => {
    if (!visible) return;

    const nextStatus = getEffectiveReviewStatus(conversation);
    setReviewStatus(nextStatus === REVIEW_STATUS.PENDING ? REVIEW_STATUS.SUCCESS : nextStatus);
    setFailureReason(conversation && conversation.failureReason ? conversation.failureReason : undefined);
    setFailureNote(conversation && conversation.failureNote ? conversation.failureNote : '');
  }, [conversation, visible]);

  const isFailure = reviewStatus === REVIEW_STATUS.FAILURE;
  const canSave = !isFailure || Boolean(failureReason);

  const handleSave = () => {
    if (!canSave) return;
    onSave({ reviewStatus, failureReason, failureNote });
  };

  return (
    <Modal
      title="Review assistant conversation"
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={loading}
      okButtonProps={{ disabled: !canSave }}
      okText="Save review"
      cancelText="Cancel"
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Radio.Group value={reviewStatus} onChange={(event) => setReviewStatus(event.target.value)}>
          <Radio.Button value={REVIEW_STATUS.SUCCESS}>Success</Radio.Button>
          <Radio.Button value={REVIEW_STATUS.FAILURE}>Failure</Radio.Button>
        </Radio.Group>

        {isFailure && (
          <>
            <Select
              placeholder="Failure category"
              style={{ width: '100%' }}
              value={failureReason}
              onChange={setFailureReason}
            >
              {FAILURE_REASONS.map((reason) => (
                <Option key={reason.value} value={reason.value}>
                  {reason.label}
                </Option>
              ))}
            </Select>
            <TextArea
              rows={4}
              placeholder="Optional note"
              value={failureNote}
              onChange={(event) => setFailureNote(event.target.value)}
            />
          </>
        )}
      </Space>
    </Modal>
  );
}

export default AssistantConversationReviewModal;
